import { SLOW_CALL, apiRequest, storedClaims } from "@common/api";
import { isCloudBoyAdmin } from "@common/store/slices/authSlice";

// Client for play-api's admin routes; the API refuses anyone outside the
// admin group, this only decides whether to show the page.

export interface AdminMember {
  sub: string;
  email: string | null;
  name: string | null;
  player: boolean;
  admin: boolean;
}

export interface SaveFile {
  name: string;
  size: number;
  updatedAt: string;
}

/** The signed-in account's sub and whether its token carries the admin group. */
export function signedInAdmin(): { sub: string | null; admin: boolean } {
  const claims = storedClaims();
  const groups = (claims?.["cognito:groups"] as string[] | undefined) ?? [];
  return { sub: typeof claims?.sub === "string" ? claims.sub : null, admin: isCloudBoyAdmin(groups) };
}

const json = (body: unknown): RequestInit => ({
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

/** When an area was last deployed, as deploy.sh recorded it; `at` is null for one never deployed. */
export interface Deploy {
  area: "ui" | "api" | "terraform";
  at: string | null;
  by: string | null;
}

/** One of CloudBoy's Lambda functions; `lastInvokedAt` is the newest event in its log group, null when it has none. */
export interface LambdaFunction {
  name: string;
  runtime: string | null;
  lastModified: string | null;
  lastInvokedAt: string | null;
}

/** "cover" saved, "miss" (libretro has none) or "skip" (no console with art, or no game file inside). */
export interface CoverSync {
  outcome: "cover" | "miss" | "skip";
  system: string | null;
  title: string | null;
}

/** One of CloudBoy's log lines: the API's own, or one of its requests. */
export interface LogLine {
  at: string;
  level: "error" | "warn" | "info";
  text: string;
}

export interface LogQuery {
  source: "api" | "requests";
  minutes: number;
  problems: boolean;
  q: string;
}

/** An address that has used CloudBoy: sessions are visits at least 5 minutes apart. The kast-dev
 * visitor-log standard's shape, so snake_case. */
export interface Visitor {
  ip: string;
  count: number;
  first_seen: string | null;
  last_seen: string | null;
}

export const admin = {
  /** Most recent first, at most 1000. */
  visitors: async (): Promise<Visitor[]> => (await apiRequest("/admin/visitors")).json(),
  /** Newest first, at most 300. */
  logs: async ({ source, minutes, problems, q }: LogQuery): Promise<LogLine[]> =>
    (await apiRequest(`/admin/logs?${new URLSearchParams({ source, minutes: String(minutes), problems: String(problems), q })}`, {}, SLOW_CALL)).json(),
  deploys: async (): Promise<Deploy[]> => (await apiRequest("/admin/deploys")).json(),
  lambdas: async (): Promise<LambdaFunction[]> => (await apiRequest("/admin/lambdas")).json(),
  members: async (): Promise<AdminMember[]> => (await apiRequest("/admin/members")).json(),
  addMember: async (email: string): Promise<AdminMember> =>
    (await apiRequest("/admin/members", { method: "POST", ...json({ email }) })).json(),
  removeMember: async (sub: string) => {
    await apiRequest(`/admin/members/${encodeURIComponent(sub)}`, { method: "DELETE" });
  },
  setAdmin: async (sub: string, isAdmin: boolean) => {
    await apiRequest(`/admin/members/${encodeURIComponent(sub)}/admin`, { method: "PUT", ...json({ admin: isAdmin }) });
  },
  saves: async (sub: string): Promise<SaveFile[]> => (await apiRequest(`/admin/saves/${encodeURIComponent(sub)}`)).json(),
  deleteSave: async (sub: string, name: string) => {
    await apiRequest(`/admin/saves/${encodeURIComponent(sub)}/${encodeURIComponent(name)}`, { method: "DELETE" });
  },
  /** How many saves and players' play times moved with it, and whether its cover did. */
  renameRom: async (id: string, name: string): Promise<{ saves: number; cover: boolean; plays: number }> =>
    (await apiRequest(`/admin/library/${encodeURIComponent(id)}/rename`, { method: "POST", ...json({ name }) }, SLOW_CALL)).json(),
  /** Finds one library game's box art and saves it as its cover. */
  syncCover: async (id: string): Promise<CoverSync> =>
    (await apiRequest(`/admin/covers/${encodeURIComponent(id)}`, { method: "POST" }, SLOW_CALL)).json(),
  deleteRom: async (id: string) => {
    await apiRequest(`/library/${encodeURIComponent(id)}`, { method: "DELETE" });
  },
};
